import { angleOf, type Pt } from '../model/geom';
import type { BlockDef, BlockRefEntity, CadEntity } from '../model/entity';
import type { CadOp } from '../ops/operation';
import {
  explodeBlockRef,
  makeBlockRef,
  readParams,
  resolveBlockDef,
  type CatalogBlock,
} from '../blocks/catalog';
import type { Command, CmdContext, CommandRunner } from './types';

/**
 * 블록 명령 (작업지시서 P4-03)
 *   INSERT · EXPLODE
 *
 * INSERT 는 카탈로그에서 고른 블록을 반복 배치한다.
 * 매대를 줄줄이 놓는 작업이 하루의 대부분이므로 Enter 칠 때까지 계속 배치한다.
 *
 * ⚠️ 어떤 블록을 넣을지는 명령이 스스로 정하지 않는다.
 *    블록 라이브러리 패널이 골라서 `ctx.pendingBlock` 에 담아 준다 —
 *    명령행에서 블록명을 타이핑하게 하면 60종 코드를 외워야 한다.
 */

/** 편집기가 명령에 넘겨주는 "지금 배치할 블록" */
export interface PendingBlock {
  block: CatalogBlock;
  params: Record<string, number>;
  /** 라이브러리 패널에서 미리 정한 배율 (resizable 정적 블록의 폭 입력) */
  initialScale?: number;
}

/** CmdContext 확장 — 블록 명령만 쓴다 */
export interface BlockCmdContext extends CmdContext {
  pendingBlock?: PendingBlock | null;
}

/* ---------------------------------------------------------------- INSERT */

export const INSERT: Command = {
  name: 'INSERT',
  aliases: ['I', 'BLOCK'],
  label: '블록 넣기',
  hint: '매대·장비를 도면에 배치합니다',
  *run(ctx: CmdContext): CommandRunner {
    const pending = (ctx as BlockCmdContext).pendingBlock;
    if (!pending) {
      ctx.log('먼저 오른쪽 블록 라이브러리에서 넣을 블록을 고르세요.');
      return;
    }

    const { block, params } = pending;
    const def = resolveBlockDef(block, params);

    const ops: CadOp[] = [];
    let defRegistered = ctx.scene.blocks.has(def.code);
    let rotation = 0;
    // 라이브러리 패널에서 미리 폭을 입력했으면 그 배율로 시작한다 —
    // "규격은 고르기 전에 정한다"(P4-05 설계 원칙)를 resizable 정적 블록에도 지킨다.
    let scale = pending.initialScale && pending.initialScale > 0 ? pending.initialScale : 1;
    // 등록할 때 "변경 가능"으로 표시한 정적 블록만 삽입 시 크기를 바꿀 수 있다 —
    // 기준으로 삼을 원래 폭(widthMm)이 있어야 배율을 계산할 수 있다.
    const canResize = !!block.resizable && !!def.widthMm;

    for (;;) {
      const keywords = canResize ? ['Rotate', 'Size'] : ['Rotate'];
      const at = yield {
        type: 'point',
        message: `${def.name} 삽입점 지정 (회전 R${canResize ? ' / 크기 S' : ''} / 종료 Enter)`,
        keywords,
        allowEnter: true,
        preview: (cursor) =>
          previewOf(def, cursor, rotation, block.defaultLayer, scale),
      };

      if (at.t === 'enter' || at.t === 'esc') break;

      if (at.t === 'keyword' && at.k === 'Rotate') {
        const base = yield { type: 'point', message: '회전 기준점 지정' };
        if (base.t !== 'point') break;
        const dir = yield {
          type: 'point',
          message: '회전 각도 지정',
          base: base.p,
          preview: (cursor) =>
            previewOf(def, base.p, angleOf(base.p, cursor), block.defaultLayer, scale),
        };
        if (dir.t === 'angle') rotation = dir.rad;
        else if (dir.t === 'point') rotation = angleOf(base.p, dir.p);
        continue;
      }

      if (at.t === 'keyword' && at.k === 'Size') {
        const want = yield {
          type: 'text',
          message: `새 폭(mm) — 지금은 ${Math.round((def.widthMm ?? 0) * scale)}mm`,
        };
        if (want.t === 'text') {
          const v = Number(want.s.trim().replace(/,/g, ''));
          if (Number.isFinite(v) && v > 0 && def.widthMm) {
            scale = v / def.widthMm;
            ctx.log(`크기 배율: ×${scale.toFixed(3)}`);
          } else {
            ctx.log('폭은 0보다 큰 숫자여야 합니다.');
          }
        }
        continue;
      }

      if (at.t !== 'point') continue;

      // 블록 정의는 문서에 한 번만 넣는다
      if (!defRegistered) {
        ops.push({
          kind: 'meta',
          command: 'INSERT',
          before: { blocks: ctx.scene.doc.blocks.map((b) => ({ ...b })) },
          after: { blocks: [...ctx.scene.doc.blocks.map((b) => ({ ...b })), def] },
        });
        defRegistered = true;
      }

      const ref = makeBlockRef(block, {
        at: at.p,
        rotation,
        params,
        scale,
        layer: ctx.currentLayer === 'A-DIM' ? block.defaultLayer : ctx.currentLayer,
      });
      // 파라메트릭 변형 코드를 실제 정의와 맞춘다
      ref.blockCode = def.code;

      ops.push({ kind: 'add', command: 'INSERT', entities: [ref] });
    }

    return ops;
  },
};

function previewOf(
  def: BlockDef,
  at: Pt,
  rotation: number,
  layer: string,
  scale = 1
): CadEntity[] {
  const ghost: BlockRefEntity = {
    id: 'preview',
    type: 'blockref',
    layer,
    blockCode: def.code,
    at,
    rotation,
    scale,
    attributes: {},
  };
  // 미리보기는 정의가 아직 문서에 없을 수 있으므로 직접 펼친다
  const defs = new Map<string, BlockDef>([[def.code, def]]);
  return explodeBlockRef(ghost, defs).map((e, i) => ({ ...e, id: `preview-${i}` }));
}

/* --------------------------------------------------------------- EXPLODE */

export const EXPLODE: Command = {
  name: 'EXPLODE',
  aliases: ['X'],
  label: '블록 분해',
  hint: '블록을 개별 선으로 풉니다 (속성은 사라집니다)',
  *run(ctx: CmdContext): CommandRunner {
    const sel = yield { type: 'selection', message: '분해할 블록 선택', useExisting: true };
    if (sel.t !== 'selection') return;

    const refs = sel.ids
      .map((id) => ctx.scene.get(id))
      .filter((e): e is BlockRefEntity => e?.type === 'blockref');

    if (refs.length === 0) {
      ctx.log('분해할 블록이 없습니다. 블록을 선택하세요.');
      return;
    }

    const withAttrs = refs.filter((r) =>
      Object.keys(r.attributes).some((k) => !k.startsWith('p:'))
    );
    if (withAttrs.length > 0) {
      ctx.log(
        `⚠️ ${withAttrs.length}개 블록의 속성(매대번호 등)이 사라집니다. 되돌리려면 Ctrl+Z 를 누르세요.`
      );
    }

    const ops: CadOp[] = [];
    let total = 0;

    for (const ref of refs) {
      const pieces = explodeBlockRef(ref, ctx.scene.blocks);
      if (pieces.length === 0) {
        ctx.log(`블록 정의를 찾지 못해 건너뜁니다: ${ref.blockCode}`);
        continue;
      }
      ops.push({ kind: 'add', command: 'EXPLODE', entities: pieces });
      ops.push({ kind: 'delete', command: 'EXPLODE', entities: [ref] });
      total += pieces.length;
    }

    if (ops.length > 0) ctx.log(`블록 ${refs.length}개 → 도형 ${total}개로 분해`);
    return ops;
  },
};

/* ------------------------------------------------------------- BLOCKPROPS */

export const BLOCKPROPS: Command = {
  name: 'BLOCKPROPS',
  aliases: ['BP'],
  label: '블록 속성',
  hint: '매대번호·구역 같은 정보를 입력합니다',
  *run(ctx: CmdContext): CommandRunner {
    const sel = yield { type: 'selection', message: '속성을 입력할 블록 선택', useExisting: true };
    if (sel.t !== 'selection') return;

    const refs = sel.ids
      .map((id) => ctx.scene.get(id))
      .filter((e): e is BlockRefEntity => e?.type === 'blockref');

    if (refs.length === 0) {
      ctx.log('블록을 선택하세요.');
      return;
    }

    const key = yield { type: 'text', message: '속성 이름 (예: 매대번호, 구역)' };
    if (key.t !== 'text' || key.s.trim() === '') return;

    const value = yield { type: 'text', message: `${key.s.trim()} 값` };
    if (value.t !== 'text') return;

    const k = key.s.trim();
    if (k.startsWith('p:')) {
      ctx.log("'p:' 로 시작하는 이름은 규격 파라미터 전용입니다.");
      return;
    }

    return [
      {
        kind: 'update',
        command: 'BLOCKPROPS',
        before: refs,
        after: refs.map((r) => ({
          ...r,
          attributes: { ...r.attributes, [k]: value.s },
        })),
      },
    ];
  },
};

/* -------------------------------------------------------------------------- */

export const BLOCK_COMMANDS: Command[] = [INSERT, EXPLODE, BLOCKPROPS];

/** 블록 참조에서 사용자 속성만 (규격 파라미터 제외) */
export function userAttributes(ref: BlockRefEntity): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(ref.attributes)) {
    if (!k.startsWith('p:')) out[k] = v;
  }
  return out;
}

export { readParams };
